import { useEffect, useRef } from 'react';
import { afterPageLoad } from '../lib/afterPageLoad';
import {
  TAU, rand, soften, makeHead, makeLeaf, makeGlow, makeDaisy, makePoppy, makeCloud,
  makeWings, SPECIES, GRAIN,
} from './meadow/sprites';

// Morning Meadow: light mode home background (v2).
// The same sunflower meadow as the night scene, by day. The light follows the
// visitor's clock (golden in the morning and evening, bright at midday). Faint
// mountains sit in the distance, clouds drift, and butterflies take over from
// the fireflies. Sunflowers face the sun, or the visitor when they move.
// `transition` plays a sunrise or sunset when the theme is switched.

export type SunTransition = 'sunrise' | 'sunset' | null;

type Leaf = { t: number; side: 1 | -1; size: number; droop: number };
type Flower = {
  x: number; h: number; r: number; layer: number; phase: number; tilt: number;
  yaw: number; turn: number; head: HTMLCanvasElement; leaves: Leaf[];
  sx: number; sy: number; // head position on screen, last frame
};
type Bloom = { x: number; y: number; size: number; phase: number; sprite: HTMLCanvasElement; stem: number };
type Blade = { x: number; h: number; w: number; lean: number; phase: number; col: string };
type Cloud = { x: number; y: number; w: number; v: number; a: number; sprite: HTMLCanvasElement };
type Mote = { x: number; y: number; vy: number; r: number; phase: number };
type Butterfly = {
  x: number; y: number; vx: number; vy: number; heading: number; flap: number; span: number;
  wings: HTMLCanvasElement; tx: number; ty: number; retarget: number; perch: Flower | null;
  perchT: number; state: 'fly' | 'perch'; companion: boolean; phase: number;
};

const clamp = (v: number, a = 0, b = 1) => Math.max(a, Math.min(b, v));
const lerp = (a: number, b: number, k: number) => a + (b - a) * k;
const hex = (h: string) => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
const mix = (a: string, b: string, k: number) => {
  const A = hex(a), B = hex(b);
  return `rgb(${A.map((v, i) => Math.round(lerp(v, B[i], k))).join(',')})`;
};
const mixRgb = (a: string, b: string, k: number) => {
  const A = a.match(/\d+/g)!.map(Number), B = hex(b);
  return `rgb(${A.map((v, i) => Math.round(lerp(v, B[i], k))).join(',')})`;
};

// Sky colours: top, middle, horizon.
const MIDDAY = ['#f8fafc', '#e4eff7', '#f1f3ea'];
const GOLDEN = ['#fbf8f2', '#f8e5cb', '#fbd29a'];
const DUSK = ['#56648f', '#bf8891', '#f19c63'];

// Sun height (0 at the horizon, 1 at noon) and its left-to-right position, from the clock.
// For previews, `?hour=8` (or any hour) overrides the clock.
function daylight(d = new Date()) {
  const q = Number(new URLSearchParams(window.location.search).get('hour'));
  const h = q > 0 && q < 24 ? q : d.getHours() + d.getMinutes() / 60;
  if (h >= 6 && h <= 19.5) return { e: Math.sin((Math.PI * (h - 6)) / 13.5), u: (h - 6) / 13.5 };
  // Light mode outside daylight hours: a soft golden light, sun high on the right.
  return { e: 0.45, u: 0.8 };
}

// 1D value noise, for mountain ridges.
function makeNoise() {
  const p = Array.from({ length: 256 }, () => Math.random());
  const n = (x: number) => {
    const i = Math.floor(x), f = x - i, u = f * f * (3 - 2 * f);
    return lerp(p[i & 255], p[(i + 1) & 255], u);
  };
  return (x: number) => {
    let s = 0, amp = 1, fr = 1, norm = 0;
    for (let o = 0; o < 5; o++) {
      const r = 1 - Math.abs(2 * n(x * fr + o * 17) - 1);
      s += r * r * amp; norm += amp; amp *= 0.5; fr *= 2.1;
    }
    return s / norm;
  };
}

export default function MorningMeadow({ className = '', transition = null }: { className?: string; transition?: SunTransition }) {
  const backRef = useRef<HTMLCanvasElement>(null);
  const frontRef = useRef<HTMLCanvasElement>(null);
  const trans = useRef<{ mode: SunTransition; start: number }>({ mode: null, start: 0 });

  useEffect(() => {
    trans.current = { mode: transition, start: performance.now() };
  }, [transition]);

  useEffect(() => {
    const canvas = backRef.current;
    const fcanvas = frontRef.current;
    if (!canvas || !fcanvas) return;
    const ctx = canvas.getContext('2d')!;
    const fctx = fcanvas.getContext('2d')!;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const HAZE = '226, 234, 238';
    const heads = [
      Array.from({ length: 3 }, () => soften(makeHead(0.5, HAZE), 5)),
      Array.from({ length: 3 }, () => soften(makeHead(0.2, HAZE), 2.5)),
      Array.from({ length: 4 }, () => makeHead(0)),
      Array.from({ length: 2 }, () => soften(makeHead(0), 2)),
    ];
    const leaves = [soften(makeLeaf(0.5, HAZE), 4), soften(makeLeaf(0.2, HAZE), 2), makeLeaf(0), makeLeaf(0)];
    const daisies = Array.from({ length: 3 }, makeDaisy);
    const poppies = Array.from({ length: 3 }, makePoppy);
    const clouds = Array.from({ length: 4 }, makeCloud);
    const sunGlow = makeGlow('255, 238, 205', 0.5);
    const lightGlow = makeGlow('255, 248, 230', 0.35);
    const goldGlow = makeGlow('255, 205, 100', 0.35);
    const wingSets = {
      monarch: makeWings(SPECIES.monarch), blue: makeWings(SPECIES.blue), white: makeWings(SPECIES.white),
      lemon: makeWings(SPECIES.lemon), zuzu: makeWings(SPECIES.zuzu),
    };

    const mountains = document.createElement('canvas');
    const hills = document.createElement('canvas');
    let MARGIN = 80;

    let W = 0, H = 0, dpr = 1, t = 0, raf = 0;
    let flowers: Flower[] = [], fgFlowers: Flower[] = [], wild: Bloom[] = [], fgWild: Bloom[] = [];
    let blades: Blade[] = [], fgBlades: Blade[] = [], cloudList: Cloud[] = [], motes: Mote[] = [];
    let flies: Butterfly[] = [], orbs: { x: number; y: number; r: number; a: number; phase: number }[] = [];
    let sun = daylight();
    const pointer = { x: 0, y: 0, last: -1e9, tapX: 0, tapY: 0, tapT: -1e9 };
    const trail: { x: number; y: number; life: number }[] = [];

    const buildLand = () => {
      MARGIN = Math.max(80, W * 0.07);
      const LW = W + MARGIN * 2;
      const ridge = makeNoise();
      const detail = makeNoise();

      // Faint mountains in three ranges, with a valley opening in the middle.
      // Each range gets sunlit and shaded faces, rock gullies and haze; the
      // far range has snow on its highest peaks.
      mountains.width = LW * dpr;
      mountains.height = H * dpr;
      const m = mountains.getContext('2d')!;
      m.setTransform(dpr, 0, 0, dpr, 0, 0);
      const STEP = 2;
      const range = (baseY: number, amp: number, scale: number, off: number, shade: string, lit: string, base: string, snow: number) => {
        const ys: number[] = [];
        for (let px = 0; px <= LW; px += STEP) {
          const xn = px / LW;
          const valley = 0.3 + 0.7 * clamp((Math.abs(xn - 0.5) - 0.04) / 0.32);
          ys.push(baseY - ridge(px * scale + off) * amp * valley);
        }
        // Light comes from the broad shape of each slope; fine rock detail is
        // added after, so the shading stays smooth.
        const n0 = ys.length;
        const light: number[] = ys.map((_, i) => {
          const a = ys[Math.max(i - 10, 0)], b = ys[Math.min(i + 10, n0 - 1)];
          return clamp(((a - b) / (20 * STEP)) * 1.6, -1, 1);
        });
        const smooth = ys.slice();
        for (let i = 0; i < n0; i++) ys[i] -= (detail(i * STEP * scale * 4 + off) - 0.5) * amp * 0.06;
        const n = ys.length;
        const body = new Path2D();
        body.moveTo(0, H);
        ys.forEach((y, i) => body.lineTo(i * STEP, y));
        body.lineTo(LW, H);
        body.closePath();
        const g = m.createLinearGradient(0, baseY - amp, 0, baseY + 10);
        g.addColorStop(0, shade);
        g.addColorStop(1, base);
        m.fillStyle = g;
        m.fill(body);

        m.save();
        m.clip(body);
        for (let i = 0; i < n; i++) {
          const y = smooth[i] - amp * 0.04, depth = (baseY - y) * 0.75 + 4, l = light[i];
          if (Math.abs(l) < 0.05) continue;
          const fg = m.createLinearGradient(0, y, 0, y + depth);
          if (l > 0) {
            fg.addColorStop(0, `rgba(${lit}, ${l * 0.7})`);
            fg.addColorStop(1, `rgba(${lit}, 0)`);
          } else {
            fg.addColorStop(0, `rgba(62, 82, 140, ${-l * 0.3})`);
            fg.addColorStop(0.7, `rgba(62, 82, 140, ${-l * 0.1})`);
            fg.addColorStop(1, 'rgba(62, 82, 140, 0)');
          }
          m.fillStyle = fg;
          m.fillRect(i * STEP, y, STEP + 0.6, depth);
        }
        // Rock gullies running down the slopes.
        m.lineWidth = 0.9;
        for (let k = 0; k < n / 14; k++) {
          const i = Math.random() * n | 0;
          if (baseY - ys[i] < amp * 0.25) continue;
          let gx = i * STEP, gy = ys[i] + 3;
          const len = (baseY - ys[i]) * rand(0.2, 0.5);
          const dir = light[i] > 0 ? -1 : 1;
          m.strokeStyle = `rgba(62, 82, 140, ${rand(0.05, 0.1)})`;
          m.beginPath();
          m.moveTo(gx, gy);
          for (let d = 0; d < len; d += 4) {
            gx += dir * rand(1.5, 3.5);
            gy += 4;
            m.lineTo(gx, gy);
          }
          m.stroke();
        }
        if (snow > 0) {
          const line = baseY - amp * snow;
          for (let i = 0; i < n; i++) {
            const y = ys[i];
            if (y >= line) continue;
            const depth = (line - y) * 0.8 + 3;
            const a = 0.5 + 0.4 * clamp(light[i] + 0.5);
            const sg = m.createLinearGradient(0, y, 0, y + depth);
            sg.addColorStop(0, `rgba(255, 255, 255, ${a})`);
            sg.addColorStop(1, 'rgba(255, 255, 255, 0)');
            m.fillStyle = sg;
            m.fillRect(i * STEP, y, STEP + 0.6, depth);
          }
        }
        m.restore();
      };
      const fog = (from: number, to: number, a: number) => {
        const f = m.createLinearGradient(0, from, 0, to);
        f.addColorStop(0, 'rgba(242, 246, 250, 0)');
        f.addColorStop(1, `rgba(242, 246, 250, ${a})`);
        m.fillStyle = f;
        m.fillRect(0, 0, LW, to);
      };
      range(H * 0.52, H * 0.3, 0.0026, 0, '#8aa3d2', '244, 248, 255', '#dde6f3', 0.62);
      fog(H * 0.3, H * 0.55, 0.45);
      range(H * 0.57, H * 0.2, 0.004, 40, '#7792c6', '226, 236, 250', '#d0dcec', 0);
      fog(H * 0.4, H * 0.59, 0.35);
      range(H * 0.61, H * 0.1, 0.0055, 90, '#88a6ba', '218, 232, 236', '#c8dad6', 0);
      fog(H * 0.48, H * 0.62, 0.4);

      // Rolling hills and the meadow, with grass texture and far flower specks.
      hills.width = LW * dpr;
      hills.height = H * dpr;
      const h = hills.getContext('2d')!;
      h.setTransform(dpr, 0, 0, dpr, 0, 0);
      const hill = (baseY: number, amp: number, freq: number, ph: number, top: string, bot: string, detail: number, specks: number) => {
        const yAt = (x: number) => baseY - (Math.sin(x * freq + ph) * 0.5 + 0.5) * amp - Math.sin(x * freq * 2.7 + ph * 2) * amp * 0.2;
        const g = h.createLinearGradient(0, baseY - amp, 0, H);
        g.addColorStop(0, top);
        g.addColorStop(1, bot);
        h.fillStyle = g;
        h.beginPath();
        h.moveTo(0, H);
        for (let x = 0; x <= LW; x += 6) h.lineTo(x, yAt(x));
        h.lineTo(LW, H);
        h.fill();
        const greens = ['60, 110, 40', '120, 170, 70', '150, 195, 90', '80, 140, 50'];
        for (let i = 0; i < detail; i++) {
          const x = Math.random() * LW;
          const y0 = yAt(x);
          const y = y0 + Math.pow(Math.random(), 1.6) * (H - y0);
          const s = 1 + Math.pow(y / H, 3) * 6;
          h.strokeStyle = `rgba(${greens[Math.random() * 4 | 0]}, ${rand(0.2, 0.45)})`;
          h.lineWidth = Math.max(0.6, s * 0.25);
          h.beginPath();
          h.moveTo(x, y);
          h.lineTo(x + rand(-0.4, 0.4) * s, y - s * rand(1.2, 2.4));
          h.stroke();
        }
        const cols = ['255, 255, 255', '255, 255, 255', '255, 214, 90', '245, 140, 50'];
        for (let i = 0; i < specks; i++) {
          const x = Math.random() * LW;
          const y0 = yAt(x);
          const y = y0 + Math.random() * (H - y0) * 0.5;
          h.fillStyle = `rgba(${cols[Math.random() * 4 | 0]}, ${rand(0.5, 0.9)})`;
          h.beginPath();
          h.arc(x, y, 0.5 + (y / H) * 1.4, 0, TAU);
          h.fill();
        }
        return yAt;
      };
      const tree = (x: number, y: number, s: number, autumn: boolean) => {
        h.fillStyle = '#5e4330';
        h.beginPath();
        h.moveTo(x - s * 0.07, y + 2);
        h.lineTo(x - s * 0.04, y - s * 0.55);
        h.lineTo(x + s * 0.04, y - s * 0.55);
        h.lineTo(x + s * 0.07, y + 2);
        h.fill();
        const tones = autumn ? ['#a8541f', '#cf7430', '#e99a48', '#f6bf6a'] : ['#3d7030', '#4f8a3a', '#67a347', '#8cc062'];
        // Dark inner mass first, lighter clumps on top, lit from above.
        for (let layer = 0; layer < 4; layer++) {
          for (let i = 0; i < 7; i++) {
            const a = rand(0, TAU), d = rand(0, 0.32) * s * (1 - layer * 0.15);
            h.fillStyle = tones[layer];
            h.beginPath();
            h.arc(x + Math.cos(a) * d, y - s * 0.85 + Math.sin(a) * d * 0.8 - layer * s * 0.06, s * rand(0.16, 0.26) * (1 - layer * 0.12), 0, TAU);
            h.fill();
          }
        }
        // Soft shadow on the grass.
        h.fillStyle = 'rgba(40, 80, 30, 0.18)';
        h.beginPath();
        h.ellipse(x + s * 0.1, y + 2, s * 0.4, s * 0.08, 0, 0, TAU);
        h.fill();
      };
      hill(H * 0.62, H * 0.07, 0.004, 1, '#b8d48f', '#a9cc7c', 900, 120);
      const mid = hill(H * 0.69, H * 0.07, 0.003, 3, '#a2cc66', '#90bf55', 1800, 380);
      for (let i = 0; i < 5; i++) {
        const x = (i < 3 ? rand(0.01, 0.2) : rand(0.8, 0.99)) * LW;
        tree(x, mid(x) + 8, rand(46, 70), i === 1);
      }
      hill(H * 0.78, H * 0.035, 0.002, 5, '#90c655', '#6aa83a', 5200, 700);
    };

    const build = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      W = canvas.clientWidth || window.innerWidth;
      H = canvas.clientHeight || window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      fcanvas.width = W;
      fcanvas.height = H;
      const mobile = W < 768;
      const k = mobile ? 0.55 : 1;
      buildLand();

      // Sunflowers, as in the night scene: far and middle rows across the whole
      // field, big in-focus ones on the sides, huge blurred ones in the corners.
      const mk = (layer: number, n: number, pickX: () => number, r: [number, number], h: [number, number]): Flower[] =>
        Array.from({ length: n }, () => {
          const rr = rand(...r) * (mobile ? 0.72 : 1);
          const set = heads[layer];
          return {
            x: pickX(), r: rr, h: rand(...h) * H, layer, phase: Math.random() * TAU, tilt: rand(-0.3, 0.3),
            yaw: rand(-0.5, 0.5), turn: 0, head: set[Math.random() * set.length | 0], sx: 0, sy: 0,
            leaves: Array.from({ length: 2 + (Math.random() * 3 | 0) }, (_, i) => ({
              t: rand(0.2, 0.7), side: (i % 2 ? 1 : -1) as 1 | -1, size: rr * rand(1.1, 1.7), droop: rand(0.2, 0.7),
            })),
          };
        });
      const any = () => rand(-0.03, 1.03) * W;
      const sides = () => (Math.random() < 0.5 ? rand(-0.03, 0.2) : rand(0.8, 1.03)) * W;
      const corner = () => (Math.random() < 0.5 ? rand(-0.08, 0.1) : rand(0.9, 1.08)) * W;
      flowers = [
        ...mk(0, Math.round(46 * k), any, [7, 13], [0.08, 0.17]),
        ...mk(1, Math.round(24 * k), any, [15, 24], [0.12, 0.22]),
        ...mk(2, Math.round(9 * k) || 4, sides, [36, 56], [0.24, 0.44]),
      ].sort((a, b) => a.layer - b.layer || b.h - a.h);
      fgFlowers = mk(3, mobile ? 2 : 4, corner, [90, 140], [0.1, 0.24]);

      const mkWild = (n: number, y: [number, number], size: [number, number]): Bloom[] =>
        Array.from({ length: n }, () => ({
          x: any(), y: rand(...y) * H, size: rand(...size) * (mobile ? 0.8 : 1), phase: Math.random() * TAU,
          sprite: Math.random() < 0.68 ? daisies[Math.random() * 3 | 0] : poppies[Math.random() * 3 | 0], stem: rand(10, 34),
        })).sort((a, b) => a.y - b.y);
      wild = mkWild(Math.round(150 * k), [0.84, 1.0], [10, 22]);
      fgWild = mkWild(Math.round(16 * k), [0.9, 1.04], [44, 70]).map(b => ({ ...b, x: corner() }));

      const greens = ['#4f8a36', '#5f9c3e', '#6fae47', '#3f7a2e'];
      const mkBlades = (n: number, pickX: () => number, h: [number, number], w: [number, number]): Blade[] =>
        Array.from({ length: n }, () => ({
          x: pickX(), h: rand(...h) * H, w: rand(...w), lean: rand(-0.4, 0.4), phase: Math.random() * TAU,
          col: greens[Math.random() * greens.length | 0],
        }));
      blades = mkBlades(Math.round(180 * k), any, [0.03, 0.1], [1, 2.2]);
      fgBlades = mkBlades(Math.round(30 * k), corner, [0.12, 0.3], [4, 8]);

      cloudList = Array.from({ length: mobile ? 3 : 5 }, (_, i) => ({
        x: rand(-0.1, 1) * W, y: rand(0.05, 0.3) * H, w: rand(0.22, 0.4) * W * (mobile ? 1.6 : 1),
        v: rand(0.04, 0.12), a: rand(0.55, 0.9), sprite: clouds[i % clouds.length],
      }));
      motes = Array.from({ length: Math.round(50 * k) }, () => ({
        x: Math.random() * W, y: rand(0.3, 1) * H, vy: rand(0.04, 0.16), r: rand(0.6, 1.5), phase: Math.random() * TAU,
      }));
      orbs = Array.from({ length: Math.round(8 * k) }, () => ({
        x: Math.random() * W, y: rand(0.78, 1.02) * H, r: rand(30, 80), a: rand(0.1, 0.22), phase: Math.random() * TAU,
      }));

      const kinds: (keyof typeof wingSets)[] = ['monarch', 'white', 'blue', 'lemon', 'monarch', 'white', 'blue'];
      const count = mobile ? 3 : 6;
      flies = Array.from({ length: count + 1 }, (_, i) => {
        const companion = i === count;
        return {
          x: rand(0.1, 0.9) * W, y: rand(0.45, 0.9) * H, vx: 0, vy: 0, heading: 0, flap: Math.random() * TAU,
          span: (companion ? 40 : rand(28, 38)) * (mobile ? 0.8 : 1),
          wings: wingSets[companion ? 'zuzu' : kinds[i % kinds.length]],
          tx: rand(0.1, 0.9) * W, ty: rand(0.45, 0.9) * H, retarget: 0, perch: null, perchT: 0,
          state: 'fly' as const, companion, phase: Math.random() * TAU,
        };
      });
    };

    const drawFlower = (c: CanvasRenderingContext2D, f: Flower, px: number, wind: number, active: boolean, sunX: number) => {
      const par = [4, 8, 16, 30][f.layer];
      const baseX = f.x - px * par;
      const sway = (Math.sin(t * 0.7 + f.phase + f.x * 0.004) * 0.05 + wind * 0.04) * (1.3 - f.layer * 0.25);
      // Face the sun, or the visitor while they move.
      const aim = active ? pointer.x : sunX;
      f.turn += (clamp((aim - baseX) / (W * 0.45), -1, 1) - f.turn) * 0.02;
      const topX = baseX + Math.sin(sway) * f.h + f.turn * f.r * 0.4;
      const topY = H - f.h * Math.cos(sway);
      const ctrlX = baseX + Math.sin(sway) * f.h * 0.3;
      f.sx = topX;
      f.sy = topY;

      const stemW = Math.max(1.4, f.r * 0.13);
      c.lineCap = 'round';
      c.strokeStyle = ['#7a9a6a', '#5d8a45', '#4a7f32', '#3f7a2e'][f.layer];
      c.lineWidth = stemW;
      c.beginPath();
      c.moveTo(baseX, H + 6);
      c.quadraticCurveTo(ctrlX, H - f.h * 0.55, topX, topY + f.r * 0.2);
      c.stroke();
      if (f.layer >= 2) {
        c.strokeStyle = 'rgba(210, 240, 160, 0.45)';
        c.lineWidth = stemW * 0.3;
        c.beginPath();
        c.moveTo(baseX - stemW * 0.25, H + 6);
        c.quadraticCurveTo(ctrlX - stemW * 0.25, H - f.h * 0.55, topX - stemW * 0.25, topY + f.r * 0.2);
        c.stroke();
      }
      const leaf = leaves[f.layer];
      for (const l of f.leaves) {
        const u = l.t;
        const lx = (1 - u) * (1 - u) * baseX + 2 * (1 - u) * u * ctrlX + u * u * topX;
        const ly = H - f.h * u;
        c.save();
        c.translate(lx, ly);
        c.rotate((l.side > 0 ? -0.2 + l.droop : Math.PI + 0.2 - l.droop) + sway + Math.sin(t * 1.1 + f.phase + u * 5) * 0.04);
        if (l.side < 0) c.scale(1, -1);
        c.drawImage(leaf, 0, -l.size * 0.36, l.size, l.size * 0.72);
        c.restore();
      }
      const size = f.r * 2.8;
      c.save();
      c.translate(topX, topY);
      c.rotate(sway * 0.8 + f.tilt * 0.4 + f.turn * 0.12);
      c.scale(Math.max(0.35, Math.cos(f.yaw + f.turn * 0.5)), 0.9);
      c.drawImage(f.head, -size / 2, -size / 2, size, size);
      c.restore();
    };

    const drawWild = (c: CanvasRenderingContext2D, list: Bloom[], px: number, par: number) => {
      c.strokeStyle = '#4f8a36';
      c.lineWidth = 1.2;
      for (const b of list) {
        const sw = Math.sin(t * 0.9 + b.phase + b.x * 0.01) * b.size * 0.12;
        const bx = b.x - px * par + sw;
        c.beginPath();
        c.moveTo(b.x - px * par, b.y + b.stem);
        c.lineTo(bx, b.y);
        c.stroke();
        c.save();
        c.translate(bx, b.y);
        c.scale(1, 0.72);
        c.drawImage(b.sprite, -b.size / 2, -b.size / 2, b.size, b.size);
        c.restore();
      }
    };

    const drawBlades = (c: CanvasRenderingContext2D, list: Blade[], px: number, par: number, wind: number) => {
      c.lineCap = 'round';
      for (const b of list) {
        const bx = b.x - px * par;
        const sw = (Math.sin(t * 1.1 + b.phase + b.x * 0.01) * 0.12 + wind * 0.08 + b.lean) * b.h;
        c.strokeStyle = b.col;
        c.lineWidth = b.w;
        c.beginPath();
        c.moveTo(bx, H + 4);
        c.quadraticCurveTo(bx + sw * 0.2, H - b.h * 0.6, bx + sw, H - b.h);
        c.stroke();
      }
    };

    const updateButterfly = (b: Butterfly, active: boolean) => {
      const tapping = t - pointer.tapT < 2.5;
      const perchX = (f: Flower) => f.sx;
      const perchY = (f: Flower) => f.sy - f.r * 0.1;
      const perchable = (f: Flower) => f.layer >= 1;

      if (b.companion) {
        // Your butterfly follows the cursor at the same easy pace as the night
        // firefly, flies to where you tap, and rests on a sunflower when you stop.
        if (tapping) {
          b.state = 'fly'; b.perch = null;
          b.tx = pointer.tapX + 26; b.ty = pointer.tapY - 20;
        } else if (active) {
          b.state = 'fly'; b.perch = null;
          b.tx = pointer.x + 26; b.ty = pointer.y - 20;
        } else if (!b.perch || !perchable(b.perch)) {
          b.state = 'fly';
          let best: Flower | null = null, bd = Infinity;
          for (const f of flowers) {
            if (f.layer < 2) continue;
            const d = Math.hypot(f.sx - b.x, f.sy - b.y);
            if (d < bd) { bd = d; best = f; }
          }
          b.perch = best;
          if (!best) { b.tx = W * 0.5 + Math.sin(t * 0.3) * W * 0.18; b.ty = H * 0.6; }
        }
      } else if (b.state === 'perch' && b.perch) {
        b.perchT -= 1 / 60;
        const near = active && Math.hypot(pointer.x - b.x, pointer.y - b.y) < 70;
        if (b.perchT <= 0 || near || !perchable(b.perch)) {
          b.state = 'fly'; b.perch = null; b.vy = -1.6; b.retarget = 0;
        }
      } else if (t > b.retarget || (b.perch && !perchable(b.perch))) {
        b.retarget = t + rand(2, 5);
        const perches = flowers.filter(perchable);
        if (Math.random() < 0.35 && perches.length) {
          b.perch = perches[Math.random() * perches.length | 0];
        } else {
          b.perch = null;
          b.tx = rand(0.05, 0.95) * W;
          b.ty = rand(0.4, 0.92) * H;
        }
      }

      if (b.state === 'perch' && b.perch) {
        b.x = perchX(b.perch);
        b.y = perchY(b.perch);
        b.heading += (Math.sin(t * 0.6 + b.phase) * 0.25 - b.heading) * 0.05;
        b.flap += 0.045;
        return;
      }

      const tx = b.perch ? perchX(b.perch) : b.tx;
      const ty = b.perch ? perchY(b.perch) : b.ty;
      const dx = tx - b.x, dy = ty - b.y;
      const d = Math.hypot(dx, dy) || 1;
      if (b.companion) {
        // Same easing as the firefly: close 3.5% of the gap every frame.
        b.vx = dx * 0.035;
        b.vy = dy * 0.035;
        b.x += b.vx;
        b.y += b.vy + Math.sin(t * 9 + b.phase) * 0.3;
        if (Math.hypot(b.vx, b.vy) > 0.6 && trail.length < 40) trail.push({ x: b.x, y: b.y, life: 1 });
      } else {
        b.vx += (dx / d * 1.1 - b.vx) * 0.04 + (Math.random() - 0.5) * 0.35;
        b.vy += (dy / d * 1.1 - b.vy) * 0.04 + (Math.random() - 0.5) * 0.35;
        if (active) {
          const ax = b.x - pointer.x, ay = b.y - pointer.y;
          const ad = Math.hypot(ax, ay) || 1;
          if (ad < 70) { b.vx += ax / ad * 0.25; b.vy += ay / ad * 0.25; }
        }
        b.x += b.vx;
        b.y += b.vy + Math.sin(t * 13 + b.phase) * 0.45;
      }
      if (b.perch && d < 8) {
        b.state = 'perch';
        b.perchT = rand(3, 7);
        b.vx = b.vy = 0;
      }
      // Face the way it flies; drift upright when nearly still.
      const moving = Math.hypot(b.vx, b.vy) > 0.3;
      const want = moving ? Math.atan2(b.vy, b.vx) + Math.PI / 2 : Math.sin(t * 0.8 + b.phase) * 0.3;
      let diff = want - b.heading;
      diff = Math.atan2(Math.sin(diff), Math.cos(diff));
      b.heading += diff * (moving ? 0.12 : 0.04);
      b.flap += moving || !b.companion ? 0.6 : 0.3;
    };

    const drawButterfly = (c: CanvasRenderingContext2D, b: Butterfly) => {
      const fold = b.state === 'perch'
        ? 0.25 + 0.75 * (0.5 + 0.5 * Math.sin(b.flap))
        : 0.12 + 0.88 * Math.abs(Math.cos(b.flap));
      const k = b.span / 2 / 100;
      c.save();
      c.translate(b.x, b.y);
      c.rotate(b.heading);
      // Soft shadow on the air below, to lift it off the scene.
      c.fillStyle = 'rgba(40, 60, 30, 0.08)';
      c.beginPath();
      c.ellipse(2, 6, b.span * 0.4 * fold, b.span * 0.25, 0, 0, TAU);
      c.fill();
      for (const s of [-1, 1]) {
        c.save();
        c.scale(s * fold, 1);
        c.drawImage(b.wings, -6 * k, -60 * k, 120 * k, 120 * k);
        c.restore();
      }
      c.fillStyle = '#1f1711';
      c.beginPath();
      c.ellipse(0, b.span * 0.02, b.span * 0.045, b.span * 0.25, 0, 0, TAU);
      c.fill();
      c.beginPath();
      c.arc(0, -b.span * 0.25, b.span * 0.05, 0, TAU);
      c.fill();
      c.strokeStyle = '#1f1711';
      c.lineWidth = 0.7;
      for (const s of [-1, 1]) {
        c.beginPath();
        c.moveTo(0, -b.span * 0.27);
        c.quadraticCurveTo(s * b.span * 0.06, -b.span * 0.42, s * b.span * 0.13, -b.span * 0.5);
        c.stroke();
        c.beginPath();
        c.arc(s * b.span * 0.13, -b.span * 0.5, 0.9, 0, TAU);
        c.fill();
      }
      c.restore();
    };

    const frame = () => {
      if (!mountains.width || !hills.width) return;
      t += 1 / 60;
      if (Math.floor(t) % 60 === 0) sun = daylight();

      // Sun height, animated during a sunrise or sunset.
      let e = sun.e;
      const tr = trans.current;
      if (tr.mode) {
        const k = clamp((performance.now() - tr.start) / (tr.mode === 'sunrise' ? 3400 : 2600));
        e = tr.mode === 'sunrise' ? lerp(-0.3, sun.e, 1 - Math.pow(1 - k, 3)) : lerp(sun.e, -0.35, k * k);
      }
      const warmth = clamp(1 - Math.max(0, e) * 1.5);
      const dusk = clamp(-e / 0.3);
      const sunX = W * (0.15 + 0.7 * sun.u);
      const sunY = H * 0.6 - e * H * 0.5;

      const active = t - pointer.last < 4;
      const px = active ? pointer.x / W - 0.5 : 0;
      const wind = Math.sin(t * 0.3) * 0.5 + Math.sin(t * 0.83) * 0.2;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Sky.
      const cols = [0, 1, 2].map(i => {
        const day = mix(MIDDAY[i], GOLDEN[i], warmth);
        return dusk > 0 ? mixRgb(day, DUSK[i], dusk) : day;
      });
      const g = ctx.createLinearGradient(0, 0, 0, H * 0.7);
      g.addColorStop(0, cols[0]);
      g.addColorStop(0.3, cols[0]);
      g.addColorStop(0.62, cols[1]);
      g.addColorStop(1, cols[2]);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);

      // Sun, glow and soft rays.
      ctx.globalCompositeOperation = 'lighter';
      const gr = H * (0.55 + 0.25 * warmth);
      ctx.globalAlpha = 0.35 + 0.35 * warmth;
      ctx.drawImage(sunGlow, sunX - gr, sunY - gr, gr * 2, gr * 2);
      ctx.globalAlpha = 0.03 * (0.4 + warmth);
      ctx.fillStyle = '#fff3d8';
      for (let i = 0; i < 8; i++) {
        const a = -Math.PI / 2 + (i - 3.5) * 0.3 + Math.sin(t * 0.05 + i) * 0.04;
        ctx.beginPath();
        ctx.moveTo(sunX, sunY);
        ctx.lineTo(sunX + Math.cos(a - 0.06) * W * 1.2, sunY + Math.sin(a - 0.06) * W * 1.2);
        ctx.lineTo(sunX + Math.cos(a + 0.06) * W * 1.2, sunY + Math.sin(a + 0.06) * W * 1.2);
        ctx.fill();
      }
      const sr = H * 0.05;
      ctx.globalAlpha = 0.9;
      ctx.drawImage(sunGlow, sunX - sr * 2.2, sunY - sr * 2.2, sr * 4.4, sr * 4.4);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = mix('#fffdf6', '#fff0c8', warmth);
      ctx.beginPath();
      ctx.arc(sunX, sunY, sr, 0, TAU);
      ctx.fill();

      for (const c of cloudList) {
        c.x += c.v;
        if (c.x > W + 40) c.x = -c.w - 40;
        ctx.globalAlpha = c.a;
        ctx.drawImage(c.sprite, c.x - px * 4, c.y, c.w, c.w * 0.43);
      }
      ctx.globalAlpha = 1;

      // Land: faint mountains, haze in the sky colour, then hills and meadow.
      const LW = W + MARGIN * 2;
      ctx.drawImage(mountains, -MARGIN - px * 6, 0, LW, H);
      const hz = ctx.createLinearGradient(0, H * 0.3, 0, H * 0.62);
      hz.addColorStop(0, 'rgba(0,0,0,0)');
      hz.addColorStop(1, cols[2]);
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = hz;
      ctx.fillRect(0, 0, W, H * 0.62);
      ctx.globalAlpha = 1;
      ctx.drawImage(hills, -MARGIN - px * 12, 0, LW, H);

      // Field: far sunflowers, wildflowers and grass, then the focus sunflowers.
      let i = 0;
      for (; i < flowers.length && flowers[i].layer < 2; i++) drawFlower(ctx, flowers[i], px, wind, active, sunX);
      drawWild(ctx, wild, px, 12);
      drawBlades(ctx, blades, px, 14, wind);
      for (; i < flowers.length; i++) drawFlower(ctx, flowers[i], px, wind, active, sunX);

      // Warm light at golden hour, and a cool fade at dusk.
      if (warmth > 0) {
        ctx.fillStyle = `rgba(255, 190, 120, ${warmth * 0.07})`;
        ctx.fillRect(0, 0, W, H);
      }
      if (dusk > 0) {
        ctx.fillStyle = `rgba(35, 40, 85, ${dusk * 0.4})`;
        ctx.fillRect(0, 0, W, H);
      }

      // Pollen in the light.
      for (const m of motes) {
        m.y -= m.vy;
        m.x += Math.sin(t * 0.5 + m.phase) * 0.15;
        if (m.y < H * 0.25) { m.y = H + 5; m.x = Math.random() * W; }
        ctx.fillStyle = `rgba(255, 238, 190, ${0.35 + 0.35 * Math.sin(t * 1.3 + m.phase)})`;
        ctx.beginPath();
        ctx.arc(m.x - px * 12, m.y, m.r, 0, TAU);
        ctx.fill();
      }

      for (const b of flies) updateButterfly(b, active);
      // A faint golden trail behind your butterfly.
      for (let j = trail.length - 1; j >= 0; j--) {
        const p = trail[j];
        p.life -= 0.03;
        if (p.life <= 0) { trail.splice(j, 1); continue; }
        ctx.fillStyle = `rgba(245, 180, 60, ${p.life * 0.55})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y + 4, 1.4 * p.life + 0.3, 0, TAU);
        ctx.fill();
      }
      for (const b of flies) {
        if (b.companion) {
          const r = b.span * 1.1;
          ctx.globalAlpha = 0.35;
          ctx.drawImage(goldGlow, b.x - r, b.y - r, r * 2, r * 2);
          ctx.globalAlpha = 1;
        }
        drawButterfly(ctx, b);
      }

      // Front layer (blurred by CSS): out-of-focus flowers, grass and light.
      fctx.clearRect(0, 0, W, H);
      drawBlades(fctx, fgBlades, px, 40, wind);
      drawWild(fctx, fgWild, px, 40);
      for (const f of fgFlowers) drawFlower(fctx, f, px, wind, active, sunX);
      fctx.globalCompositeOperation = 'lighter';
      for (const o of orbs) {
        fctx.globalAlpha = o.a * (0.6 + 0.4 * Math.sin(t * 0.5 + o.phase)) * (0.4 + warmth * 0.6);
        fctx.drawImage(lightGlow, o.x - o.r - px * 40, o.y - o.r, o.r * 2, o.r * 2);
      }
      fctx.globalAlpha = 1;
      fctx.globalCompositeOperation = 'source-over';
      if (dusk > 0) {
        fctx.globalCompositeOperation = 'source-atop';
        fctx.fillStyle = `rgba(35, 40, 85, ${dusk * 0.45})`;
        fctx.fillRect(0, 0, W, H);
        fctx.globalCompositeOperation = 'source-over';
      }
    };

    const loop = () => {
      frame();
      raf = requestAnimationFrame(loop);
    };
    const start = () => { if (!raf && !reduced) raf = requestAnimationFrame(loop); };
    const stop = () => { cancelAnimationFrame(raf); raf = 0; };

    const onMove = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      pointer.x = e.clientX - r.left;
      pointer.y = e.clientY - r.top;
      pointer.last = t;
    };
    const onDown = (e: PointerEvent) => {
      onMove(e);
      pointer.tapX = pointer.x;
      pointer.tapY = pointer.y;
      pointer.tapT = t;
    };
    const onVis = () => (document.hidden ? stop() : start());
    let resizeTimer = 0;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => { build(); frame(); }, 150);
    };

    // The first still frame draws right away; the movement starts once the page has loaded.
    build();
    frame();
    const cancelStart = afterPageLoad(start);
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerdown', onDown, { passive: true });
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelStart();
      stop();
      clearTimeout(resizeTimer);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return (
    <div aria-hidden="true" className={`overflow-hidden pointer-events-none ${className}`}>
      <canvas ref={backRef} className="absolute inset-0 block w-full h-full" />
      <canvas ref={frontRef} className="absolute inset-0 block w-full h-full" style={{ filter: 'blur(6px)' }} />
      <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{ backgroundImage: GRAIN }} />
    </div>
  );
}
